import React, { useState } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { useTranslation } from 'react-i18next';

import QuotaManagementTab from './components/QuotaManagementTab';
import FactoryManagementTab from './components/FactoryManagementTab';
import SupplySidePendingOrdersTab from './components/SupplySidePendingOrdersTab';
import ActiveBookingsTab from './components/ActiveBookingsTab';
import VehicleManagementTab from './components/VehicleManagementTab';
import UserApprovalsTab from './components/UserApprovalsTab';
import BuyersTab from './components/BuyersTab';
import SellOffersTab from './components/SellOffersTab';
import TransportAgentsTab from './components/TransportAgentsTab';
import VehicleSubstitutionsTab from './components/VehicleSubstitutionsTab';
import InvoiceCenterTab from './components/InvoiceCenterTab';
import ReportsCenterQMTab from './components/ReportsCenterQMTab';

import { useNavigate } from 'react-router-dom';

const QuotaAllocationPricingCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tabGroups = [
    {
      id: 'operations',
      label: 'Operations',
      icon: 'Activity',
      subTabs: [
        { id: 'quotas', label: 'Quotas', icon: 'Package' },
        { id: 'pending', label: 'Pending Orders', icon: 'Clock' },
        { id: 'active', label: 'Active Bookings', icon: 'CheckCircle' },
        { id: 'sell-offers', label: 'Sell Offers', icon: 'Tag' },
        { id: 'vehicle-changes', label: 'Vehicle Changes', icon: 'RefreshCw' },
        { id: 'invoice-center', label: 'Invoice Center', icon: 'FileText' },
        { id: 'reports', label: 'Reports', icon: 'BarChart2' },
      ]
    },
    {
      id: 'fleet',
      label: 'Fleet & Transport',
      icon: 'Truck',
      subTabs: [
        { id: 'agents', label: 'Transport Agents', icon: 'Users' },
        { id: 'vehicles', label: 'Vehicles', icon: 'Truck' },
      ]
    },
    {
      id: 'admin',
      label: 'Administration',
      icon: 'Settings',
      subTabs: [
        { id: 'factories', label: 'Factories', icon: 'Building2' },
        { id: 'approvals', label: 'User Approvals', icon: 'UserCheck' },
        { id: 'buyers', label: 'Buyers', icon: 'Users' },
      ]
    },
  ];

  const [activeGroup, setActiveGroup] = useState(() => {
    const stored = localStorage.getItem('quotaActiveGroup');
    return ['operations', 'fleet', 'admin']?.includes(stored) ? stored : 'operations';
  });

  const [activeSubTab, setActiveSubTab] = useState(() => {
    const stored = localStorage.getItem('quotaActiveSubTab');
    return stored || 'quotas';
  });

  const handleGroupChange = (groupId) => {
    setActiveGroup(groupId);
    localStorage.setItem('quotaActiveGroup', groupId);
    const firstSubTab = tabGroups?.find(g => g?.id === groupId)?.subTabs?.[0]?.id;
    if (firstSubTab) {
      setActiveSubTab(firstSubTab);
      localStorage.setItem('quotaActiveSubTab', firstSubTab);
    }
  };

  const handleSubTabChange = (subTabId) => {
    setActiveSubTab(subTabId);
    localStorage.setItem('quotaActiveSubTab', subTabId);
  };

  const renderSubTabContent = () => {
    switch (activeSubTab) {
      case 'quotas':
        return <QuotaManagementTab />;
      case 'pending':
        return <SupplySidePendingOrdersTab />;
      case 'active':
        return <ActiveBookingsTab />;
      case 'sell-offers':
        return <SellOffersTab />;
      case 'vehicle-changes':
        return <VehicleSubstitutionsTab />;
      case 'invoice-center':
        return <InvoiceCenterTab />;
      case 'reports':
        return <ReportsCenterQMTab />;
      case 'agents':
        return <TransportAgentsTab />;
      case 'vehicles':
        return <VehicleManagementTab />;
      case 'factories':
        return <FactoryManagementTab />;
      case 'approvals':
        return <UserApprovalsTab />;
      case 'buyers':
        return <BuyersTab />;
      default:
        return <QuotaManagementTab />;
    }
  };

  const currentGroup = tabGroups?.find(g => g?.id === activeGroup);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#faf7f2' }}>
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="mb-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center">
                <Icon name="Settings" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1f2937' }}>{t('quotaManager.title')}</h1>
                <p className="text-sm" style={{ color: '#6b7280' }}>{t('quotaManager.subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2">
              <div className="flex gap-2">
                {tabGroups?.map((group) => (
                  <button
                    key={group?.id}
                    onClick={() => handleGroupChange(group?.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                      activeGroup === group?.id
                        ? 'shadow-md'
                        : 'hover:bg-gray-50'
                    }`}
                    style={activeGroup === group?.id 
                      ? { backgroundColor: '#f59e0b', color: '#000000' }
                      : { color: '#6b7280' }
                    }
                  >
                    <Icon name={group?.icon} size={18} />
                    <span className="hidden sm:inline">{group?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="hidden sm:block">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-1 p-2 overflow-x-auto">
                  {currentGroup?.subTabs?.map((subTab) => (
                    <button
                      key={subTab?.id}
                      onClick={() => handleSubTabChange(subTab?.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                        activeSubTab === subTab?.id
                          ? 'text-black' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      style={activeSubTab === subTab?.id ? { backgroundColor: '#fef3c7' } : {}}
                    >
                      <Icon name={subTab?.icon} size={16} />
                      <span>{subTab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="block sm:hidden">
              <div className="relative">
                <select
                  value={activeSubTab}
                  onChange={(e) => handleSubTabChange(e?.target?.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-amber-300 rounded-xl font-semibold appearance-none cursor-pointer focus:ring-2 focus:ring-amber-500 focus:border-amber-500 shadow-sm"
                  style={{ color: '#1f2937' }}
                >
                  {currentGroup?.subTabs?.map((subTab) => (
                    <option key={subTab?.id} value={subTab?.id}>
                      {subTab?.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <Icon name="ChevronDown" size={18} className="text-amber-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300">
            {renderSubTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuotaAllocationPricingCenter;
